import { api } from "@/lib/api";

export const sleep = (ms: number) => new Promise((r) => setTimeout(r, ms));
export const FATAL = [400, 403, 413, 415, 422];

// Resumable chunked upload: each chunk is retried, so a weak mobile signal doesn't lose the whole file.
export async function chunkedUpload(file: File, signal: AbortSignal, onProgress: (p: number, phase: "upload" | "processing") => void, prevId?: string, purpose = "content"): Promise<any> {
  let uploadId = prevId;
  let offset = 0;
  let chunk = 4 * 1024 * 1024;
  if (uploadId) {
    try { offset = (await api.get(`/media/uploads/${uploadId}`, { signal })).data.received; } catch { uploadId = undefined; }
  }
  if (!uploadId) {
    const { data } = await api.post("/media/uploads", { filename: file.name, content_type: file.type, size: file.size }, { signal });
    uploadId = data.upload_id;
    chunk = data.chunk_size;
  }
  const fail = (e: any) => Object.assign(e, { uploadId });
  while (offset < file.size) {
    const blob = file.slice(offset, offset + chunk);
    for (let attempt = 0; ; attempt++) {
      try {
        const { data } = await api.put(`/media/uploads/${uploadId}`, blob, { params: { offset }, signal, headers: { "Content-Type": "application/octet-stream" }, timeout: 120000,
          onUploadProgress: (e) => onProgress(Math.round(((offset + (e.loaded || 0)) / file.size) * 90), "upload") });
        offset = data.received;
        break;
      } catch (e: any) {
        const st = e?.response?.status;
        const m = /^offset:(\d+)$/.exec(e?.response?.data?.detail || "");
        if (st === 409 && m) { offset = +m[1]; break; }
        if (signal.aborted || (st && st < 500 && st !== 408 && st !== 429) || attempt >= 5) throw fail(e);
        await sleep(1500 * (attempt + 1));
      }
    }
    onProgress(Math.round((offset / file.size) * 90), "upload");
  }
  // Server processes in the background; poll until the file is really stored (never report success early).
  onProgress(92, "processing");
  let st: any;
  for (let attempt = 0; ; attempt++) {
    try { st = (await api.post(`/media/uploads/${uploadId}/complete`, null, { params: { purpose }, signal, timeout: 60000 })).data; break; }
    catch (e: any) { const c = e?.response?.status; if (signal.aborted || (c && c < 500 && c !== 408 && c !== 429) || attempt >= 5) throw fail(e); await sleep(2000 * (attempt + 1)); }
  }
  for (let misses = 0; st.state === "finalizing"; ) {
    await sleep(2000);
    try { st = (await api.get(`/media/uploads/${uploadId}`, { signal, timeout: 30000 })).data; misses = 0; }
    catch (e: any) { if (signal.aborted || e?.response?.status === 410 || ++misses > 15) throw fail(e); }
  }
  if (st.state === "failed") throw fail({ response: { status: st.error_status || 500, data: { detail: st.error } } });
  onProgress(100, "processing");
  return st.media;
}
